import { RouteModule } from '#/horizon/components/RouteModule';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18nLocale } from '#/i18n/types/I18nLocale';
import type { LayoutHandleProps } from '#/layouts/Layout/LayoutHandleProps';
import { salariesApiFetchJobSynonym } from '#/modules/salaries/api/salariesApiFetchJobSynonym';
import { searchJobsRoutePathByLocale } from '#/modules/search/routing/search.route-paths';
import type { searchJobsRouteReference } from '#/modules/search/routing/search.routes';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import type { HorizonAppRouteShouldRevalidateFunctionArgs } from '#/routing/types/HorizonAppRouteShouldRevalidateFunctionArgs';
import { getStaticImageSrc } from '#/utils/getImageSrc';

import { SearchJobsPageLoadable } from './SearchJobsPageLoadable';

export const handle = {
  urlByLocale: searchJobsRoutePathByLocale,
  defaultSearchType: 'JOB',
  hideFooterDivider: true,
  searchFromPageType: 'SEARCH',
  showSearchRefinements: true,
  showDesktopSearch: true,
  isSearchPage: true,
} satisfies LayoutHandleProps;

export async function loader({
  request,
}: HorizonAppRouteLoaderArgs<typeof searchJobsRouteReference>) {
  try {
    const url = new URL(request.url);

    const synonymId = url.searchParams.get('synonymId');

    const synonym = synonymId
      ? await salariesApiFetchJobSynonym(synonymId)
      : null;

    return { synonym };
  } catch {
    // @TODO: Should we log and/or throw an error?
    return { synonym: null };
  }
}

export function shouldRevalidate({
  currentUrl,
  defaultShouldRevalidate,
  nextUrl,
}: HorizonAppRouteShouldRevalidateFunctionArgs<
  typeof searchJobsRouteReference
>) {
  if (currentUrl.searchParams.size === nextUrl.searchParams.size) {
    return false;
  }

  return defaultShouldRevalidate;
}

export default function SearchJobsHorizonRoute(
  _: HorizonAppRouteComponentProps<typeof loader>,
) {
  const { i18n, locale } = useI18n();

  const ogImage = (() => {
    switch (locale) {
      case I18nLocale.EN:
        return getStaticImageSrc('og-image-en-jobs.jpg');
      case I18nLocale.ES:
        return;
      case I18nLocale.PT:
        return getStaticImageSrc('og-image-pt-jobs.jpg');
    }
  })();

  return (
    <RouteModule
      title={
        /* TRANSLATORS: job search page title */
        i18n('Best Jobs Near You, Remote Jobs, Nonprofit Jobs')
      }
      ogImage={ogImage}
    >
      <SearchJobsPageLoadable />
    </RouteModule>
  );
}
